/**
 * CompanyLink Component
 * A company reference input component with search, open, and clear buttons
 * 
 * @description Provides a styled company reference input for company lookups:
 * - Search (magnifying glass): Open CompanyPicker modal to search and select a company
 * - Open (building): Open the selected company in a new form/tab
 * - Clear (X): Remove the company reference
 * 
 * This matches the ContactLink pattern but for Company references (FieldTypeId: 20).
 * 
 * @example
 * ```typescript
 * const companyLink = new CompanyLink({
 *   id: 'client-company',
 *   label: 'Client Company',
 *   onCompanyChange: (id, name) => console.log('Company changed:', id, name)
 * });
 * await companyLink.initialize();
 * container.appendChild(companyLink.getElement());
 * ```
 */

import { IComponent } from './IComponent';
import { CompanyPicker, type CompanyPickerApiService } from './CompanyPicker';
import { Logger } from '@/services/Logger';
import { ToasterService } from '@/services/ToasterService';

const logger = Logger.scope('CompanyLink');

/**
 * Configuration options for the CompanyLink component
 */
export interface CompanyLinkOptions {
  /** Unique ID for the input element */
  id: string;
  
  /** Optional label displayed above the input */
  label?: string;
  
  /** Color variant for buttons */
  color?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
  
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  
  /** Whether the input is disabled */
  disabled?: boolean;
  
  /** Whether the input is readonly (no search/clear, only open) */
  readonly?: boolean;
  
  /** Placeholder text when no company is selected */
  placeholder?: string;
  
  /** Callback when company changes */
  onCompanyChange?: (companyId: number | null, companyName: string | null) => void;
  
  /** Callback when company is opened */
  onCompanyOpen?: (companyId: number) => void;
  
  /** Custom open handler (overrides default behavior) */
  customOpenHandler?: (companyId: number) => Promise<void>;
  
  /** Show label above input */
  showLabel?: boolean;
  
  /** Additional CSS classes for the container */
  containerClass?: string;
  
  /** Company IDs to exclude from search results */
  excludeCompanyIds?: number[];
  
  /** API service for CompanyPicker (tenant-specific) */
  apiService?: CompanyPickerApiService;
  
  /** Hide the search button */
  hideSearchButton?: boolean;
  
  /** Hide the open button */
  hideOpenButton?: boolean;
  
  /** Hide the clear button */
  hideClearButton?: boolean;
}

/**
 * CompanyLink Component - Company reference input with search/open/clear buttons
 */
export class CompanyLink implements IComponent {
  private options: CompanyLinkOptions;
  private element: HTMLElement | null = null;
  private inputElement: HTMLInputElement | null = null;
  private companyId: number | null = null;
  private companyName: string | null = null;

  constructor(options: CompanyLinkOptions) {
    this.options = {
      color: 'info',
      size: 'sm',
      disabled: false,
      readonly: false,
      placeholder: 'No company selected',
      showLabel: false,
      hideSearchButton: false,
      hideOpenButton: false,
      hideClearButton: false,
      ...options
    };
  }

  /**
   * Initialize the component
   */
  public async initialize(): Promise<void> {
    this.element = this.render();
    this.bindEvents();
    logger.debug('CompanyLink initialized', { id: this.options.id });
  }

  /**
   * Get the component's root element
   */
  public getElement(): HTMLElement {
    if (!this.element) {
      throw new Error('CompanyLink not initialized. Call initialize() first.');
    }
    return this.element;
  }

  /**
   * Render into a container element
   */
  public renderTo(container: HTMLElement): void {
    if (!this.element) {
      throw new Error('CompanyLink not initialized. Call initialize() first.');
    }
    container.appendChild(this.element);
  }

  /**
   * Get the current company ID
   */
  public getCompanyId(): number | null {
    return this.companyId;
  }

  /**
   * Get the current company name
   */
  public getCompanyName(): string | null {
    return this.companyName;
  }

  /**
   * Set the company
   */
  public setCompany(companyId: number | null, companyName: string | null): void {
    logger.debug('setCompany called', { 
      id: this.options.id, 
      companyId, 
      companyName,
      hasInputElement: !!this.inputElement 
    });
    
    this.companyId = companyId;
    this.companyName = companyName;
    if (this.inputElement) {
      this.inputElement.value = companyName || '';
      this.inputElement.setAttribute('data-company-id', companyId ? String(companyId) : '');
      logger.debug('Input element value set', { 
        id: this.options.id, 
        inputValue: this.inputElement.value 
      });
    } else {
      logger.warn('setCompany: inputElement is null!', { id: this.options.id });
    }
    this.updateButtonStates();
  }

  /**
   * Check if a company is set
   */
  public hasCompany(): boolean {
    return this.companyId !== null && this.companyId > 0;
  }

  /**
   * Enable/disable the component
   */
  public setDisabled(disabled: boolean): void {
    this.options.disabled = disabled;
    this.updateButtonStates();
  }

  /**
   * Get the value for form collection (returns the company ID)
   */
  public getValue(): number | null {
    return this.companyId;
  }

  /**
   * Set the value from form population
   */
  public setValue(companyId: number | null, displayValue?: string): void {
    this.setCompany(companyId, displayValue || null);
  }

  /**
   * Destroy the component and clean up
   */
  public destroy(): void {
    if (this.element) {
      this.element.remove();
    }
    this.element = null;
    this.inputElement = null;
  }

  /**
   * Render the component HTML
   */
  private render(): HTMLElement {
    const { id, label, size, disabled, readonly, showLabel, containerClass, placeholder,
            hideSearchButton, hideOpenButton, hideClearButton } = this.options;

    const container = document.createElement('div');
    container.className = `company-link-container ${containerClass || ''}`;

    // Label (if shown)
    if (showLabel && label) {
      const labelElement = document.createElement('label');
      labelElement.className = 'label py-0.5';
      labelElement.innerHTML = `<span class="label-text text-xs font-medium">${label}</span>`;
      container.appendChild(labelElement);
    }

    // Input group with buttons
    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'join w-full';

    // Readonly company name input
    const input = document.createElement('input');
    input.type = 'text';
    input.id = id;
    input.className = `input input-bordered input-${size} join-item flex-1`;
    input.readOnly = true;
    input.placeholder = placeholder || label || 'No company selected';
    this.inputElement = input;
    buttonGroup.appendChild(input);

    // Search button (find company)
    if (!readonly && !hideSearchButton) {
      const searchBtn = document.createElement('button');
      searchBtn.type = 'button';
      searchBtn.className = `btn btn-${size} join-item company-link-search bg-base-100 hover:bg-base-200 border border-base-content/20`;
      searchBtn.innerHTML = '<i class="fas fa-search" style="color: #3b82f6;"></i>';
      searchBtn.title = 'Search for company';
      searchBtn.disabled = disabled || false;
      buttonGroup.appendChild(searchBtn);
    }

    // Open button - blue building icon when enabled, grey when disabled
    if (!hideOpenButton) {
      const openBtn = document.createElement('button');
      openBtn.type = 'button';
      const openDisabled = disabled || !this.companyId;
      openBtn.className = `btn btn-${size} join-item company-link-open bg-base-100 border border-base-content/20 ${openDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-base-200'}`;
      openBtn.innerHTML = `<i class="fas fa-building" style="color: ${openDisabled ? '#9ca3af' : '#3b82f6'};"></i>`;
      openBtn.title = 'Open company';
      openBtn.dataset.disabled = openDisabled ? 'true' : 'false';
      buttonGroup.appendChild(openBtn);
    }

    // Clear button - red icon when enabled, grey when disabled
    if (!readonly && !hideClearButton) {
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      const clearDisabled = disabled || !this.companyId;
      clearBtn.className = `btn btn-${size} join-item company-link-clear bg-base-100 border border-base-content/20 ${clearDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-base-200'}`;
      clearBtn.innerHTML = `<i class="fas fa-times" style="color: ${clearDisabled ? '#9ca3af' : '#ef4444'};"></i>`;
      clearBtn.title = 'Clear company';
      clearBtn.dataset.disabled = clearDisabled ? 'true' : 'false';
      buttonGroup.appendChild(clearBtn);
    }

    container.appendChild(buttonGroup);

    return container;
  }

  /**
   * Bind event handlers
   */
  private bindEvents(): void {
    if (!this.element) return;

    // Search button click
    const searchBtn = this.element.querySelector('.company-link-search');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => this.handleSearch());
    }

    // Open button click
    const openBtn = this.element.querySelector('.company-link-open');
    if (openBtn) {
      openBtn.addEventListener('click', () => this.handleOpen());
    }

    // Clear button click
    const clearBtn = this.element.querySelector('.company-link-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.handleClear());
    }
  }

  /**
   * Handle search button click - open CompanyPicker modal
   */
  private async handleSearch(): Promise<void> {
    if (this.options.disabled) return;
    
    logger.debug('CompanyLink search clicked', { id: this.options.id });

    try {
      const result = await CompanyPicker.show({
        title: this.options.label ? `Select ${this.options.label}` : 'Select Company',
        excludeCompanyIds: this.options.excludeCompanyIds,
        apiService: this.options.apiService
      });

      logger.debug('CompanyPicker result received', { 
        id: this.options.id, 
        selected: result.selected, 
        companyId: result.companyId,
        companyName: result.companyName
      });

      if (result.selected && result.companyId) {
        this.setCompany(result.companyId, result.companyName || null);
        this.options.onCompanyChange?.(result.companyId, result.companyName || null);
        logger.info('Company selected', { 
          id: this.options.id, 
          companyId: result.companyId, 
          companyName: result.companyName 
        });
      }
    } catch (error) {
      logger.error('CompanyPicker failed', { error });
      ToasterService.error('Failed to open company picker');
    }
  }

  /**
   * Handle open button click - open the company record
   */
  private async handleOpen(): Promise<void> {
    if (!this.companyId) {
      ToasterService.info('No company selected');
      return;
    }

    logger.debug('CompanyLink open clicked', { 
      id: this.options.id, 
      companyId: this.companyId 
    });

    // Use custom handler if provided
    if (this.options.customOpenHandler) {
      try {
        await this.options.customOpenHandler(this.companyId);
        return;
      } catch (error) {
        logger.error('Custom open handler failed', { error });
        ToasterService.error('Failed to open company');
        return;
      }
    }

    // Default: use OpenForm to navigate to company detail
    if (typeof window.OpenForm === 'function') {
      window.OpenForm(`companyDetail?id=${this.companyId}`);
    } else {
      // Fallback: use hash navigation
      window.location.hash = `companyDetail?id=${this.companyId}`;
    }

    // Notify callback
    this.options.onCompanyOpen?.(this.companyId);
  }

  /**
   * Handle clear button click - remove the company reference
   */
  private handleClear(): void {
    if (this.options.disabled || !this.companyId) return;

    logger.debug('CompanyLink clear clicked', { 
      id: this.options.id, 
      previousCompanyId: this.companyId 
    });

    const previousId = this.companyId;
    const previousName = this.companyName;

    this.setCompany(null, null);
    this.options.onCompanyChange?.(null, null);

    logger.info('Company cleared', { 
      id: this.options.id, 
      previousCompanyId: previousId,
      previousName: previousName
    });
  }

  /**
   * Update button states based on current company and disabled state
   */
  private updateButtonStates(): void {
    if (!this.element) return;

    const { disabled } = this.options;
    const hasCompany = this.companyId !== null && this.companyId > 0;

    // Search button
    const searchBtn = this.element.querySelector('.company-link-search') as HTMLButtonElement;
    if (searchBtn) {
      searchBtn.disabled = disabled || false;
    }

    // Open button - enable only when company is selected
    const openBtn = this.element.querySelector('.company-link-open') as HTMLButtonElement;
    if (openBtn) {
      const openDisabled = disabled || !hasCompany;
      openBtn.dataset.disabled = openDisabled ? 'true' : 'false';
      openBtn.className = openBtn.className.replace(/opacity-60 cursor-not-allowed|hover:bg-base-200/g, '');
      openBtn.className += openDisabled ? ' opacity-60 cursor-not-allowed' : ' hover:bg-base-200';
      const icon = openBtn.querySelector('i');
      if (icon) {
        icon.style.color = openDisabled ? '#9ca3af' : '#3b82f6';
      }
    }

    // Clear button - enable only when company is selected
    const clearBtn = this.element.querySelector('.company-link-clear') as HTMLButtonElement;
    if (clearBtn) {
      const clearDisabled = disabled || !hasCompany;
      clearBtn.dataset.disabled = clearDisabled ? 'true' : 'false';
      clearBtn.className = clearBtn.className.replace(/opacity-60 cursor-not-allowed|hover:bg-base-200/g, '');
      clearBtn.className += clearDisabled ? ' opacity-60 cursor-not-allowed' : ' hover:bg-base-200';
      const icon = clearBtn.querySelector('i');
      if (icon) {
        icon.style.color = clearDisabled ? '#9ca3af' : '#ef4444';
      }
    }
  }
}
